import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import i18n from 'i18n-js';
import moment from 'moment';
import { withRouter } from 'react-router-dom';
import { formatAmount, getDynamicLink } from 'utils';
import { InvoiceActions } from 'state/modules/invoice';
import { getSourceItemString } from 'utils/utils';
import { Card, CollapseCard } from 'components/UI';
import ItemStatus from 'components/ItemStatus';
import { InvoiceModal } from 'components/Modals';
import { useStyles, listTextStyles, collapseCardStyles } from './ReceivableDetails.styled';
import { Box, List, Grid, ListItem, Typography, ListItemText, Divider } from '@material-ui/core';
import clsx from 'clsx';

const ReceivableDetails = ({ id, menuItems, statusHide = false, receivableInfo, receivablePayment }) => {
  const classes = useStyles();
  const listClasses = listTextStyles();
  const collapseCardClasses = collapseCardStyles();
  const receivableType = useSelector((state) => state.config?.settings?.core?.receivable?.type);

  const dispatch = useDispatch();
  const [openEditModal, setOpenEditModal] = useState(false);

  const { localeFormat } = useSelector((state) => state.organizations);
  const { current, fetchedProcessInvoice } = useSelector((state) => state.invoice);

  /**
   * Get invoice by id
   */
  const fetchInvoiceById = useCallback(
    (id) => {
      dispatch(InvoiceActions.fetchInvoiceById(id));
    },
    [dispatch]
  );

  useEffect(() => {
    if (id) {
      fetchInvoiceById(id);
    }
  }, [fetchInvoiceById, id]);

  useEffect(() => {
    if (fetchedProcessInvoice) {
      fetchInvoiceById(id);
    }
  }, [fetchInvoiceById, id, fetchedProcessInvoice]);

  useEffect(() => {
    return () => {
      dispatch(InvoiceActions.fetchInvoicesReset());
    };
  }, []);

  const receivableTitle = (
    <Typography color="textPrimary" component="span">
      <Box fontSize={14} fontWeight="fontWeightMedium" data-cy="receivable-title">
        {i18n.t(`receivable.${receivableType?.toLowerCase()}`)}
      </Box>
    </Typography>
  );

  const ListTitleComponent = ({ title, subtitle }) => {
    return (
      <Box className={classes.titleRow}>
        <Typography component="span" className={classes.title}>
          {title}
        </Typography>
        <Typography component="span" className={classes.subtitle}>
          {subtitle}
        </Typography>
      </Box>
    );
  };

  const receivableCollapse = (val) =>
    val && (
      <CollapseCard
        title={
          val?.subtitle ? (
            <ListTitleComponent title={val?.title} subtitle={val?.subtitle} />
          ) : (
            val?.title
          )
        }
        customClasses={collapseCardClasses}
        dataCy="receivable-widget-card"
      >
        <List className={classes.list}>
          {val?.items?.map((val, index) =>
            !val.hide && (
              <ListItem
                key={index}
                className={clsx(classes.listItem, val?.classes)}
                data-cy={val.dataCy}
              >
                <ListItemText
                  classes={listClasses}
                  primary={val.label}
                  secondary={
                    <Box
                      display="flex"
                      component="span"
                      flexDirection="column"
                      className={classes.textRight}
                    >
                      <Typography
                        variant="body2"
                        component="span"
                        color="textSecondary"
                        className={classes?.[val?.classes]}
                      >
                        {val.value}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            )
          )}
        </List>
      </CollapseCard>
    );

  const paymentData = (receivable) => {
    if (receivablePayment) {
      return {
        title: 'Payment',
        subtitle:
          `Paid: $${formatAmount(getTotalPaid(receivable?.paid_card, receivable?.paid_credit))}` ||
          '-',
        items: [
          {
            label: `${i18n.t('receivable.total_invoiced')}:`,
            value: `$${formatAmount(receivable?.invoiced || 0)}` || '-',
            dataCy: 'total_invoiced',
            hide: !receivablePayment.includes('total_invoiced'),
          },
          {
            label: `${i18n.t('receivable.total_paid')}:`,
            value:
              `$${formatAmount(getTotalPaid(receivable?.paid_card, receivable?.paid_credit))}` ||
              '-',
            dataCy: 'total_paid',
            hide: !receivablePayment.includes('total_paid'),
          },
          {
            label: `${i18n.t('receivable.amount_due')}:`,
            value:
              `$${formatAmount(
                (receivable?.invoiced || 0) -
                  (receivable?.paid_card || 0) -
                  (receivable?.paid_credit || 0)
              )}` || '-',
            dataCy: 'amount_due',
            hide: !receivablePayment.includes('amount_due'),
          },
        ],
      };
    }
    return null;
  };

  const receivableData = (receivable) => {
    if (receivableInfo) {
      const { source, source_id } =
        receivable?.source_name === 'SubscriptionPeriod'
          ? {
              source: receivable?.extra_source_name,
              source_id: receivable?.extra_source_id,
            }
          : { source: receivable?.source_name, source_id: receivable?.source_id };

      return {
        title: i18n.t(
          `receivable.${receivableType?.toLowerCase()}_information`
        ),
        items: [
          {
            label: `${i18n.t('receivable.from')}:`,
            value: receivable?.account_emitter?.customer_name || '-',
            dataCy: 'from',
            hide: !receivableInfo.includes('from'),
          },
          {
            label: `${i18n.t('receivable.to')}:`,
            value: getDynamicLink(
              `${receivable?.account_customer?.customer_name} ID#${receivable?.account_customer?.holder_id}`,
              receivable?.account_customer?.holder_id,
              receivable?.account_customer?.holder_type === 'User' ? 'Driver' : 'Organization'
            ),
            dataCy: 'to',
            classes: 'link',
            hide: !receivableInfo.includes('to'),
          },
          {
            label: `${i18n.t('receivable.source')}:`,
            value: getDynamicLink(
              getSourceItemString(source, source_id),
              source_id,
              source,
              undefined,
              undefined,
              receivable?.description
            ),
            dataCy: 'source',
            classes: 'link',
            hide: !receivableInfo.includes('source'),
          },
          {
            label: `${i18n.t('receivable.completed_on')}:`,
            value: receivable?.completed_on
              ? moment(receivable?.completed_on).format(`${localeFormat} h:mm A`)
              : '-',
            dataCy: 'completed_on',
          },
          {
            label: `${i18n.t('receivable.modified_on')}:`,
            value: receivable?.modified_time
              ? moment(receivable?.modified_time).format(`${localeFormat} h:mm A`)
              : '-',
            dataCy: 'modified_on',
          },
          {
            label: `${i18n.t('receivable.due_date')}:`,
            value: moment(receivable?.due_time).isValid()
              ? moment(receivable?.due_time).utc(false).format(localeFormat)
              : '-',
            dataCy: 'due_time',
            hide: !receivableInfo.includes('statement_due_date') || !receivable?.due_time,
          },
          {
            label: `${i18n.t('receivable.processed_datetime')}:`,
            value: receivable?.processed_time
              ? moment(receivable?.processed_time).format(`${localeFormat} h:mm A`)
              : '-',
            dataCy: 'due_time',
            hide:
              !receivableInfo.includes('statement_processed_datetime') ||
              !receivable?.processed_time,
          },
          {
            label: `${i18n.t('receivable.due_datetime')}:`,
            value: moment(receivable?.due_time).isValid()
              ? moment(receivable?.due_time).format(`${localeFormat}  hh:mm A`)
              : '-',
            dataCy: 'due_time',
            hide: !receivableInfo.includes('invoice_due_date') || !receivable?.due_time,
          },
          {
            label: `${i18n.t('receivable.processed_datetime')}:`,
            value: receivable?.processed_time
              ? moment(receivable?.processed_time).format(`${localeFormat} h:mm A`)
              : '-',
            dataCy: 'due_time',
            hide:
              !receivableInfo.includes('invoice_processed_datetime') || !receivable?.processed_time,
          },
        ],
      };
    }
    return null;
  };

  const getTotalPaid = (...payments) => payments.reduce((p, c) => p + (c || 0), 0);

  const handleOpenEditModal = () => {
    setOpenEditModal((prevState) => !prevState);
  };

  const defaultMenuItems = [
    {
      title: i18n.t('receivable.edit_invoice'),
      onClick: handleOpenEditModal,
      display: current?.[id]?.is_draft,
      can: { name: 'invoice', action: 'create' },
    },
  ];

  return (
    <Card
      menuItems={menuItems || defaultMenuItems}
      title={receivableTitle}
      headerActionType="vertical"
      data-cy="receivable-detail-widget"
    >
      <Box className={classes.cardContent}>
        <Grid container spacing={1} direction="row" alignItems="center">
          <Grid item xs={7}>
            <Typography variant="h3">
              <Box className={classes.name}>{getDynamicLink(`ID# ${id}`, id, receivableType)}</Box>
            </Typography>
          </Grid>
          {!statusHide && current[id]?.status && (
            <Grid item xs={5} className={`${classes.textRight}`} data-cy="receivable-status">
              <ItemStatus i18nKey="receivable.statuses" status={current[id]?.status} />
            </Grid>
          )}
        </Grid>
        <Divider className={classes.divider} light={true} />
        <div>
          {receivableCollapse(receivableData(current[id]))}
          {receivableCollapse(paymentData(current[id]))}
        </div>
      </Box>
      {openEditModal && <InvoiceModal open={openEditModal} handleClick={handleOpenEditModal} />}
    </Card>
  );
};

export default withRouter(ReceivableDetails);
